import type { ExperienceConfig } from "./experiences";
import { featureKindForPath } from "./feature-paths";
import { isComponentPath } from "./page-components-runtime";
import type { PageNode } from "./pages";
import { policyPagePaths } from "./policy-pages";

// Where a creator's pages live: reserved and built-in addresses, feature pages and templates, with
// no schema library, so a creator site's script can read them (see pages.ts).

const reservedPageRoots = new Set([
  "app",
  "api",
  "rpc",
  "billing",
  "integrations",
  "auth",
  "admin",
  "assets",
  "media",
  "preview",
  "join",
  "checkout",
  "account",
  "sign-in",
  "sign-up",
  "forgot-password",
  "reset-password",
  "blog",
  "posts",
  "library",
  "collections",
  "matchups",
  "matchup",
  "bible",
  "live",
  "community",
  "training",
  "coaching",
  "booking",
  "shop",
  "merch",
  "tournaments",
  "use-cases",
  "membership-app",
  "compare",
  "guides",
  "privacy",
  "terms",
  "404",
  "500",
  "_app",
  "_astro",
  ".well-known",
]);
// Drate's tools on drate.app (site/src/lib/tools.ts) take single addresses, so creators keep the
// rest of /tools for pages of their own. /monthly-rewards is Drate's page for creators who mail
// members something each month (site/start/routes/monthly-rewards.tsx), /for-authors its page
// for writers (site/start/routes/for-authors.tsx) and /for-gyms its page for gyms and coaches
// (site/start/routes/for-gyms.tsx).
const reservedPagePaths = new Set([
  "/tools/patreon-fee-calculator",
  "/monthly-rewards",
  "/for-authors",
  "/for-gyms",
]);

/**
 * Feature index pages a published document may compose, like Home and About. The feature keeps
 * its detail routes, data and authorization; without a published document its native page shows.
 */
// /shop remains readable for saved feature documents and old bookmarks.
export const featureIndexPaths = ["/blog", "/library", "/merch", "/shop"] as const;

/**
 * Built-in pages a published document may compose around their own content: the page's native
 * content is one `builtIn` block, so its data, actions and paid gating stay with the feature.
 * Detail pages stay native while their feature is enabled. League Bible and Matchups may be
 * replaced with independent pages when their native feature is off. Membership and the member sign-in and
 * sign-up forms are no feature switch; password recovery and the account page stay native.
 */
export const builtInBlocks = [
  "bible",
  "matchups",
  "live",
  "training",
  "coaching",
  "booking",
  "community",
  "tournaments",
  "join",
  "sign-in",
  "sign-up",
] as const;
export type BuiltInBlock = (typeof builtInBlocks)[number];
export const builtInPagePaths = builtInBlocks.map((block) => `/${block}`);

/** What each built-in page's own content is called in the editor. */
export const builtInBlockLabels: Record<BuiltInBlock, string> = {
  bible: "League Bible chapters",
  matchups: "Matchup library",
  live: "Free matchup preview",
  training: "Training module",
  coaching: "Coaching sessions",
  booking: "Appointment booking",
  community: "Community feed",
  tournaments: "Tournaments",
  join: "Membership plans",
  "sign-in": "Sign-in form",
  "sign-up": "Sign-up form",
};

/** Built-in pages every site has, whichever features are on. */
const standingPagePaths = ["/join", "/sign-in", "/sign-up"];

export function isStandingPagePath(path: string) {
  return standingPagePaths.includes(path);
}

/**
 * A feature's own content on its page (a built-in block, the guide on the guide template): it
 * moves and hides, but is never copied, duplicated or deleted, so the page always keeps it.
 */
export function isFeatureContentNode(node: PageNode) {
  return node.kind === "builtIn" || node.kind === "matchupGuide";
}

/** Feature content that stays one of the page's sections, never inside a frame. */
export function featureContentStaysSection(node: PageNode | null | undefined) {
  return (node?.kind === "builtIn" && !builtInMayNest(node.page)) || node?.kind === "matchupGuide";
}

/**
 * Whether a built-in block may sit inside the page's frames, beside the creator's own content.
 * A form can; a feature's whole page (the Bible, Matchups) stays one of the page's sections.
 */
export function builtInMayNest(page: BuiltInBlock) {
  return page === "sign-in" || page === "sign-up";
}

export function builtInBlockForPath(path: string): BuiltInBlock | null {
  return (builtInBlocks as readonly string[]).includes(path.slice(1))
    ? (path.slice(1) as BuiltInBlock)
    : null;
}

/**
 * Feature pages that become ordinary pages while their feature is off. Creators can write their
 * own League Bible or matchups from pages. With the feature off, /bible or /matchups and
 * the pages under it (/bible/runes, /matchups/aatrox) are pages of their own like /resources: shown, listed and
 * linked like any other.
 */
const ownWhenOffRoots = ["/bible", "/matchups"];
const ownWhenOffRoot = (path: string) =>
  ownWhenOffRoots.find((root) => path === root || path.startsWith(`${root}/`));

/**
 * One page for every guide: the creator designs the matchup guide template, and each guide's
 * address (/matchups/akali, and its sections) opens it filled with that guide. Its elements bind
 * to the guide's discovery fields as a card's do to its item; the guide's coaching, gating and
 * actions stay with Matchups in one guide block.
 */
export const matchupGuideTemplatePath = "/matchups/:guide";
const recordTemplatePaths = [matchupGuideTemplatePath] as const;
export type RecordTemplatePath = (typeof recordTemplatePaths)[number];

export function isRecordTemplatePath(path: string): path is RecordTemplatePath {
  return (recordTemplatePaths as readonly string[]).includes(path);
}

/**
 * Documents with no address of their own: the guide template, which every guide's address opens,
 * and components, which pages show. They never list, link or show in navigation or the sitemap.
 */
export function isUnlistedPagePath(path: string) {
  return isRecordTemplatePath(path) || isComponentPath(path);
}

/** The template a guide's address opens: /matchups/akali and /matchups/akali/mid-game. */
export function recordTemplateForPath(path: string): RecordTemplatePath | null {
  return /^\/matchups\/[a-z0-9-]{1,120}(?:\/[a-z0-9-]{1,40})?$/.test(path)
    ? matchupGuideTemplatePath
    : null;
}

/** A page under a feature's own address (/bible/runes), the creator's only while it's off. */
export function isOwnWhenOffChild(path: string) {
  const root = ownWhenOffRoot(path);
  return !!root && path !== root;
}

/** Whether this feature page is, with its feature off, an ordinary page of the creator's own. */
export function isOwnPagePath(
  path: string,
  experiences: readonly Pick<ExperienceConfig, "kind" | "enabled">[],
) {
  if (isRecordTemplatePath(path)) return false;
  const root = ownWhenOffRoot(path);
  return (
    !!root && !experiences.some((item) => item.enabled && item.kind === featureKindForPath(root))
  );
}

/** Authored feature pages follow their feature switch; ordinary pages stay available. */
export function isEnabledPagePath(
  path: string,
  experiences: readonly Pick<ExperienceConfig, "kind" | "enabled">[],
) {
  if (isStandingPagePath(path) || isOwnPagePath(path, experiences) || isComponentPath(path))
    return true;
  if (isRecordTemplatePath(path))
    return experiences.some((item) => item.enabled && item.kind === "matchups");
  // While the feature is on, its own pages (a native chapter) answer there instead.
  if (isOwnWhenOffChild(path)) return false;
  if (!(featureIndexPaths as readonly string[]).includes(path) && !builtInPagePaths.includes(path))
    return true;
  return experiences.some((item) => item.enabled && item.kind === featureKindForPath(path));
}

/**
 * Root/About, feature pages and policy pages are explicit delegates; other capability and
 * platform routes cannot be taken over.
 */
export function isCreatorPagePath(path: string) {
  if (
    path === "/" ||
    path === "/about" ||
    (featureIndexPaths as readonly string[]).includes(path) ||
    builtInPagePaths.includes(path) ||
    policyPagePaths.includes(path) ||
    isRecordTemplatePath(path) ||
    isComponentPath(path)
  )
    return true;
  if (
    path.length > 180 ||
    !/^\/[a-z0-9]+(?:-[a-z0-9]+)*(?:\/[a-z0-9]+(?:-[a-z0-9]+)*)*$/.test(path)
  )
    return false;
  // One level under a feature that turns into the creator's own pages (/bible/runes).
  if (isOwnWhenOffChild(path)) return path.split("/").length === 3;
  return !reservedPageRoots.has(path.split("/")[1]!) && !reservedPagePaths.has(path);
}

export function isSafePageLink(value: string) {
  if (!value) return true;
  if (
    [...value].some(
      (character) =>
        character.charCodeAt(0) <= 32 || character.charCodeAt(0) === 127 || character === "\\",
    )
  )
    return false;
  if (value.startsWith("/")) return !value.startsWith("//");
  if (/^#[a-zA-Z][a-zA-Z0-9_-]*$/.test(value)) return true;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !!url.hostname && !url.username && !url.password;
  } catch {
    return false;
  }
}
